import type { BillingSignedDocument } from "../../shared/billing-document-schemas";
import { SUBSCRIPTION_STATUS_LABEL } from "../../shared/billing-labels";
import { COMMERCIAL_DOCUMENT_LABELS } from "../../shared/commercial-documents";
import type {
  BillingOverview,
  BillingSubscription,
  BillingTerms,
} from "../../shared/billing-schemas";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import type { CollectKind } from "./billing-collect-request";
import { termsFromDocument } from "../../shared/billing-document-terms";
import { signedCopyHref } from "./billing-api";
import { cadenceLabel, formatUsd } from "./billing-format";
import { subscriptionBadge, subscriptionDates } from "./billing-row-actions";

/**
 * How approved terms are being billed, on their own card (ADR-0087): the subscription that bills
 * them, what has been invoiced and paid, and the next thing to do. The buttons open the collect
 * sheet already set to these terms, so nobody picks terms from a list.
 */

type Step = { label: string; kind: CollectKind; primary: boolean };

/** The actions these terms still call for, in order, limited to what the employee may do. */
function nextSteps(terms: BillingTerms, live: boolean, kinds: readonly CollectKind[]): Step[] {
  const allowed = (stripe: CollectKind, manual: CollectKind) =>
    kinds.includes(stripe) ? stripe : kinds.includes(manual) ? manual : null;
  const has = (kind: BillingTerms["lines"][number]["kind"]) =>
    terms.lines.some((line) => line.kind === kind);
  const steps: Step[] = [];
  const recurring = allowed("stripe_subscription", "manual_subscription");
  if (has("recurring") && !live && recurring)
    steps.push({ label: "Start billing", kind: recurring, primary: true });
  const invoice = allowed("stripe_invoice", "manual_invoice");
  // A fee fully covered by a credit leaves nothing to invoice.
  if (has("one_time") && terms.totals.oneTimeMinor > 0 && invoice)
    steps.push({ label: "Send an invoice", kind: invoice, primary: steps.length === 0 });
  const credit = allowed("stripe_credit", "manual_credit");
  if (has("credit") && credit)
    steps.push({ label: "Apply a credit", kind: credit, primary: false });
  if (kinds.includes("manual_invoice") && !steps.some((step) => step.kind === "manual_invoice"))
    steps.push({ label: "Record an outside invoice", kind: "manual_invoice", primary: false });
  return steps;
}

function SubscriptionLine({
  subscription,
  onOpen,
}: {
  subscription: BillingSubscription;
  onOpen: () => void;
}) {
  return (
    <li className="flex flex-wrap items-center justify-between gap-3">
      <div className="min-w-0">
        <button
          type="button"
          className="text-left font-semibold text-foreground hover:underline"
          onClick={onOpen}
        >
          {subscription.description} · {formatUsd(subscription.amountMinor)}{" "}
          {cadenceLabel(subscription.cadence)}
        </button>
        <p className="text-sm text-muted-foreground">{subscriptionDates(subscription)}</p>
      </div>
      <Badge variant={subscriptionBadge(subscription.status)}>
        {SUBSCRIPTION_STATUS_LABEL[subscription.status]}
      </Badge>
    </li>
  );
}

/** The signed document these terms came from, and anything on it billing could not carry over. */
function SignedSource({
  source,
  customerSlug,
}: {
  source: BillingSignedDocument;
  customerSlug: string;
}) {
  const unread = termsFromDocument(source, []).unread;
  const label = COMMERCIAL_DOCUMENT_LABELS[source.kind].toLowerCase();
  return (
    <div className="flex flex-col gap-1">
      <p className="m-0">
        From the signed {label}{" "}
        <a
          className="font-semibold text-link hover:underline"
          href={signedCopyHref(customerSlug, source.customerDocumentId)}
          target="_blank"
          rel="noreferrer"
        >
          {source.reference}
        </a>
        , signed {source.signedOn}.
      </p>
      {unread.length > 0 ? (
        <p className="m-0 text-sm font-semibold text-warning">
          Not carried over, check by hand: {unread.join("; ")}
        </p>
      ) : null}
    </div>
  );
}

export function DealBilling({
  terms,
  source,
  customerSlug,
  billing,
  kinds,
  onCollect,
  onOpenSubscription,
}: {
  terms: BillingTerms;
  /** The signed document these terms were set up from, if any. */
  source?: BillingSignedDocument | null;
  customerSlug: string;
  billing: BillingOverview;
  kinds: readonly CollectKind[];
  onCollect: (kind: CollectKind) => void;
  onOpenSubscription: (subscription: BillingSubscription) => void;
}) {
  // Live subscriptions first; ended ones stay listed so the card keeps its history.
  const subscriptions = billing.subscriptions
    .filter((subscription) => subscription.termsId === terms.id)
    .sort((a, b) => Number(a.status === "canceled") - Number(b.status === "canceled"));
  const live = subscriptions.some((subscription) => subscription.status !== "canceled");
  const invoices = billing.invoices.filter((invoice) => invoice.termsId === terms.id);
  const billed = invoices.reduce((total, invoice) => total + invoice.amountDueMinor, 0);
  const paid = invoices.reduce((total, invoice) => total + invoice.amountPaidMinor, 0);
  const steps = nextSteps(terms, live, kinds);
  const recurring = terms.lines.some((line) => line.kind === "recurring");
  return (
    <section
      aria-label={`Billing for ${terms.title}`}
      className="mt-5 flex flex-col gap-3 border-t border-muted pt-4"
    >
      <h4 className="text-sm font-semibold text-muted-foreground">Billing</h4>
      {source ? <SignedSource source={source} customerSlug={customerSlug} /> : null}
      {subscriptions.length > 0 ? (
        <ul className="flex flex-col gap-3" aria-label="Subscriptions">
          {subscriptions.map((subscription) => (
            <SubscriptionLine
              key={subscription.id}
              subscription={subscription}
              onOpen={() => {
                onOpenSubscription(subscription);
              }}
            />
          ))}
        </ul>
      ) : null}
      {recurring && !live ? <p>Not billing yet.</p> : null}
      <p className="text-muted-foreground">
        {invoices.length === 0
          ? "No invoices yet."
          : `${invoices.length} ${invoices.length === 1 ? "invoice" : "invoices"} · ${formatUsd(billed)} billed · ${formatUsd(paid)} paid`}
      </p>
      {steps.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {steps.map((step) => (
            <Button
              key={step.label}
              type="button"
              variant={step.primary ? "default" : "secondary"}
              onClick={() => {
                onCollect(step.kind);
              }}
            >
              {step.label}
            </Button>
          ))}
        </div>
      ) : null}
    </section>
  );
}
